import type { ReactNode } from 'react';

import { RADIUS, Squircle } from '@/shared/ui';

import styles from './ResultModal.module.scss';

export type ShapeVariant = 'trophy' | 'oops' | 'uspeh';
export type ImageVariant = 'trophy' | 'ball' | 'composition';

interface ResultModalProps {
  onClose: () => void;
  shape: ShapeVariant;
  image: ImageVariant;
  title: string;
  body: ReactNode;
  score?: ReactNode;
  caption?: ReactNode;
  testId: string;
}

const SHAPE_SRC: Record<ShapeVariant, string> = {
  trophy: '/images/results/shape-trophy.svg',
  oops: '/images/results/shape-oops.svg',
  uspeh: '/images/results/shape-uspeh.svg',
};

const IMAGE_SRC: Record<ImageVariant, string> = {
  trophy: '/images/results/top3-trophies.png',
  ball: '/images/hero/bb-ball.webp',
  composition: '/images/results/success-composition.png',
};

const SHAPE_CLASS: Record<ShapeVariant, string | undefined> = {
  trophy: styles.shapeTrophy,
  oops: styles.shapeOops,
  uspeh: styles.shapeUspeh,
};

const IMAGE_CLASS: Record<ImageVariant, string | undefined> = {
  trophy: styles.imageTrophy,
  ball: styles.imageBall,
  composition: styles.imageComposition,
};

// Сколько места сверху занять под картинку: она вне потока, и без резерва
// текст наезжает на неё, как только ему не хватает высоты снизу.
const RESERVE_CLASS: Record<ImageVariant, string | undefined> = {
  trophy: styles.reserveTrophy,
  ball: styles.reserveBall,
  composition: styles.reserveComposition,
};

/**
 * Общий каркас модалок итогов (дневных/недельных/месячных розыгрышей,
 * узлы 533:64142/533:64143/533:64170) — у всех девяти вариантов совпадает
 * всё, кроме текста, очков/фрибетов и (у семи из девяти) картинки с шейпом:
 * те три группы, где разыгрывается место в рейтинге, используют одну
 * и ту же картинку с кубками и один и тот же синий шейп — отличаются
 * только у дневных модалок (проигрыш/выигрыш конкретного Прогноза).
 */
export function ResultModal({ onClose, shape, image, title, body, score, caption, testId }: ResultModalProps) {
  return (
    <div
      className={[styles.sheet, RESERVE_CLASS[image]].filter(Boolean).join(' ')}
      data-testid={testId}
    >
      {shape === 'trophy' ? (
        // Шейп и картинка кубков уменьшены и сдвинуты вместе, одним жёстким
        // блоком (.shapeImageGroupTrophy) — не по отдельности: у них общая
        // компоновка макета, и независимое масштабирование развело бы их
        // относительно друг друга. См. комментарий в .scss.
        <div className={styles.shapeImageGroupTrophy}>
          <div className={SHAPE_CLASS[shape]}>
            {/* Рамка + врезка повторяют структуру узла 533:64154/533:64155:
                файл shape-trophy.svg — это уже клякса без полей, рамка
                задаёт ей размер и поворот из макета, врезка — где клякса
                лежит внутри рамки (см. комментарии в .scss). */}
            <div className={styles.shapeFrame}>
              <img className={styles.shapeImage} src={SHAPE_SRC[shape]} alt="" aria-hidden />
            </div>
          </div>
          <div className={IMAGE_CLASS[image]}>
            <img className={styles.pictureImage} src={IMAGE_SRC[image]} alt="" aria-hidden />
          </div>
        </div>
      ) : (
        <>
          <div className={SHAPE_CLASS[shape]}>
            <img className={styles.shapeImage} src={SHAPE_SRC[shape]} alt="" aria-hidden />
          </div>
          <div className={IMAGE_CLASS[image]}>
            <img className={styles.pictureImage} src={IMAGE_SRC[image]} alt="" aria-hidden />
          </div>
        </>
      )}

      <button type="button" className={styles.close} aria-label="Закрыть" onClick={onClose}>
        <img className={styles.closeIcon} src="/images/matches/close-light.svg" alt="" aria-hidden />
      </button>

      <div className={styles.content}>
        <div className={styles.textScore}>
          <div className={styles.text}>
            <h2 className={styles.title}>{title}</h2>
            <div className={styles.body}>{body}</div>
          </div>

          {score ? <div className={styles.score}>{score}</div> : null}
          {caption ? <div className={styles.caption}>{caption}</div> : null}
        </div>

        <Squircle asChild cornerRadius={RADIUS.xl}>
          <button type="button" className={styles.cta} onClick={onClose} data-testid={`${testId}-cta`}>
            Отлично!
          </button>
        </Squircle>
      </div>
    </div>
  );
}

/** Строка «иконка + число» в очках/шапке (узел 391:5275 «counter»). */
export function ResultCounter({ icon, value }: { icon: 'points' | 'freebets'; value: string }) {
  return (
    <span className={styles.counter}>
      <img
        className={styles.counterIcon}
        src={icon === 'points' ? '/images/icons/fire-fill.svg' : '/images/icons/freebet-light.svg'}
        alt=""
        aria-hidden
      />
      <span className={styles.counterValue}>{value}</span>
    </span>
  );
}

/** Название физического приза в строке очков (узел 438:52178, «Яндекс станция»). */
export function ResultPrizeName({ children }: { children: ReactNode }) {
  return <span className={styles.prizeName}>{children}</span>;
}
